/**
 * eBA 1.0 renewal reconciliation — provisioning beside billing.
 *
 * THE DISTINCTION THIS SCREEN EXISTS TO MAKE
 *   PROVISIONING  what the client currently has configured and enabled.
 *   BILLING       what the client is contractually charged for.
 *
 * Billing is the commercial authority. Provisioning is evidence used to
 * reconcile it. Nothing here writes a quantity on its own: adopting the
 * provisioning figure is an action an operator takes deliberately, and
 * the server reads the figure itself rather than trusting one this
 * screen sends, so "Adopt 25" can never post 2,500.
 *
 * WHY HISTORICAL FIGURES ARE SHOWN BUT NEVER OFFERED
 * A past term's licensed-user count is provenance, not a candidate. The
 * API's candidate list is built from current provisioning alone, so
 * there is no historical value in it to filter out. Where an operator
 * ruling corrects a historical figure, the ruling renders beside the
 * number — an operator cannot read the figure without also reading what
 * was decided about it.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle, Warning, Info, ArrowsLeftRight, Prohibit,
} from "@phosphor-icons/react";

/* State → colour. Kept here rather than inlined so a new server state
   renders neutrally instead of silently picking "looks fine" green. */
const TONE = {
  MATCH: { bd: "#BBF7D0", bg: "#F0FDF4", fg: "#166534" },
  CONFIRMED_NOT_STATED: { bd: "#BBF7D0", bg: "#F0FDF4", fg: "#166534" },
  MISMATCH: { bd: "#FDE68A", bg: "#FFFBEB", fg: "#92400E" },
  UNRESOLVED: { bd: "#FDE68A", bg: "#FFFBEB", fg: "#92400E" },
  NO_PROVISIONING_EVIDENCE: { bd: "#E5E5E5", bg: "#FAFAFA", fg: "#525252" },
};
const NEUTRAL = { bd: "#E5E5E5", bg: "#FAFAFA", fg: "#525252" };
const toneFor = (state) => TONE[state] || NEUTRAL;

const num = (v) => (v === null || v === undefined ? "—"
  : Number.isInteger(Number(v)) ? String(Number(v)) : String(v));

export default function Eba1ReconciliationPanel({ clientId, isAdmin, onConfirmed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [entryFor, setEntryFor] = useState(null);   // line awaiting a typed quantity
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const r = await api.get(`/renewals/clients/${clientId}/eba1-reconciliation`);
      setData(r.data);
    } catch {
      // An empty state and a failure state must never look identical.
      setData(null);
      setFailed(true);
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (line, source, quantity) => {
    setBusyId(line.recurring_item_id);
    try {
      const r = await api.post(
        `/renewals/clients/${clientId}/licence-lines/${line.recurring_item_id}/confirm-licensed-users`,
        { source, quantity: quantity ?? null },
      );
      toast.success(
        r.data.licensed_user_quantity === null
          ? "Recorded: the commercial document states no licensed-user quantity."
          : `Licensed users confirmed as ${num(r.data.licensed_user_quantity)}.`,
      );
      setEntryFor(null);
      await load();
      onConfirmed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail
        || "Could not record the licensed-user confirmation.");
    }
    setBusyId(null);
  };

  if (loading) {
    return (
      <div className="border border-[#E5E5E5] bg-white p-4 text-xs text-[#525252]"
           data-testid="eba1-reconciliation-loading">
        Loading eBA 1.0 configuration…
      </div>
    );
  }

  if (failed) {
    return (
      <div className="border border-[#FDE68A] bg-[#FFFBEB] p-4"
           data-testid="eba1-reconciliation-error">
        <p className="text-xs font-bold text-[#92400E]">
          Could not load the eBA 1.0 configuration.
        </p>
        <p className="text-[11px] text-[#525252] mt-1">
          Renewal readiness above is unaffected. Refresh to try again; if this
          continues, contact support.
        </p>
        <Button onClick={load} variant="outline"
                className="rounded-none text-xs mt-2">Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const p = data.provisioning || {};
  const lines = data.licence_lines || [];
  const summary = data.summary || {};
  const modules = data.modules || {};
  const history = data.historical_evidence || [];

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid="eba1-reconciliation">
      <div className="bg-[#FAFAFA] px-4 py-3 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-2">
          <ArrowsLeftRight size={15} className="text-[#0055FF]" aria-hidden="true" />
          <h3 className="text-sm font-bold text-[#0A0A0A]">
            Renewal configuration detected from eBA {p.is_eba1 ? "1.0" : "2.0"}
          </h3>
        </div>
        <p className="text-[10px] text-[#A3A3A3] mt-0.5">{data.authority_note}</p>
      </div>

      {!data.schema_ready && (
        <div className="px-4 py-2 bg-[#FFFBEB] border-b border-[#FDE68A] text-[11px] text-[#92400E]">
          The licensed-user confirmation schema is not installed on this database,
          so confirmation actions are unavailable until an administrator runs the
          migration.
        </div>
      )}

      {/* ── The two records, side by side ─────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5]">
        <div className="bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            eBA {p.is_eba1 ? "1.0" : "2.0"} provisioning · operational evidence
          </div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight"
               data-testid="eba1-provisioning-users">
            {num(p.legacy_user_license_limit)}
            <span className="text-xs font-bold text-[#525252] ml-1.5">licensed users</span>
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">
            {p.enabled_module_count} enabled module{p.enabled_module_count === 1 ? "" : "s"}/function
            {p.enabled_module_count === 1 ? "" : "s"}
            {p.active_licensed_user_count !== null
              && p.active_licensed_user_count !== undefined && (
              <> · {p.active_licensed_user_count} licensed user record
                {p.active_licensed_user_count === 1 ? "" : "s"} in Pulse</>
            )}
          </div>
          {!p.is_eba1 && (
            <p className="text-[10px] text-[#92400E] mt-1.5 leading-snug">
              This client is on eBA 2.0, where licence entitlements — not the legacy
              limit — are the provisioning record. The legacy figure is shown for
              reference and cannot be adopted.
            </p>
          )}
        </div>

        <div className="bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
            Current recurring specification · commercial authority
          </div>
          <div className="text-2xl font-black text-[#0A0A0A] mt-1 tracking-tight"
               data-testid="eba1-commercial-users">
            {lines.length === 0 ? "—"
              : lines.every((l) => l.commercial_licensed_user_quantity === null)
                ? (lines.every((l) => l.confirmed) ? "Not stated" : "Unresolved")
                : num(lines.reduce(
                    (t, l) => t + (l.commercial_licensed_user_quantity || 0), 0))}
            {lines.some((l) => l.commercial_licensed_user_quantity !== null) && (
              <span className="text-xs font-bold text-[#525252] ml-1.5">licensed users</span>
            )}
          </div>
          <div className="text-[11px] text-[#525252] mt-0.5">
            {lines.length} recurring licence line{lines.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* ── Per-line reconciliation + operator actions ─────────────── */}
      <div className="p-4 space-y-3">
        <p className="text-xs text-[#525252]" data-testid="eba1-summary-headline">
          {summary.headline}
        </p>

        {lines.map((line) => {
          const tone = toneFor(line.state);
          const busy = busyId === line.recurring_item_id;
          return (
            <div key={line.recurring_item_id}
                 className="border"
                 style={{ borderColor: tone.bd, background: tone.bg }}
                 data-testid={`eba1-line-${line.recurring_item_id}`}>
              <div className="px-3 py-2.5">
                <div className="flex items-start gap-2">
                  {line.state === "MATCH" || line.state === "CONFIRMED_NOT_STATED"
                    ? <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0"
                                   style={{ color: tone.fg }} aria-hidden="true" />
                    : <Warning size={15} weight="fill" className="mt-0.5 shrink-0"
                               style={{ color: tone.fg }} aria-hidden="true" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-black uppercase tracking-wider"
                       style={{ color: tone.fg }}
                       data-testid={`eba1-state-${line.recurring_item_id}`}>
                      {line.state_label}
                    </p>
                    <p className="text-xs text-[#0A0A0A] mt-0.5 font-medium break-words">
                      {line.description}
                    </p>
                    <p className="text-[11px] text-[#525252] mt-1 leading-snug">
                      {line.detail}
                    </p>
                    {line.confirmed && line.source_label && (
                      <p className="text-[10px] text-[#525252] mt-1">
                        {line.source_label}
                        {line.confirmed_at ? ` · ${String(line.confirmed_at).slice(0, 10)}` : ""}
                        {line.confirmed_by ? ` · ${line.confirmed_by}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                      Provisioning
                    </div>
                    <div className="text-sm font-black font-mono text-[#0A0A0A]">
                      {num(line.provisioning_licensed_user_limit)}
                    </div>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] mt-1">
                      Billing
                    </div>
                    <div className="text-sm font-black font-mono text-[#0A0A0A]">
                      {line.commercial_licensed_user_quantity === null
                        ? (line.confirmed ? "Not stated" : "—")
                        : num(line.commercial_licensed_user_quantity)}
                    </div>
                  </div>
                </div>

                {isAdmin && data.schema_ready && (
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {(line.available_actions || []).map((a) => (
                      <Button
                        key={a.action}
                        disabled={busy}
                        onClick={() => (a.requires_operator_input
                          ? setEntryFor(line)
                          : confirm(line, a.action, a.quantity))}
                        variant={a.action === "adopted_from_provisioning" ? "default" : "outline"}
                        className={`rounded-none text-xs ${
                          a.action === "adopted_from_provisioning"
                            ? "bg-[#0055FF] hover:bg-[#003ECC] text-white" : ""}`}
                        data-testid={`eba1-action-${a.action}-${line.recurring_item_id}`}
                      >
                        {busy ? "Saving…" : a.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {lines.length === 0 && (
          <p className="text-xs text-[#A3A3A3]">
            This client has no active recurring licence line, so there is no
            licensed-user quantity to reconcile.
          </p>
        )}
      </div>

      {/* ── Modules: context only, never a hold, never auto-created ── */}
      <div className="border-t border-[#E5E5E5] p-4">
        <div className="flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0 text-[#525252]" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wider text-[#525252]">
              {modules.state_label} · advisory
            </p>
            <p className="text-[11px] text-[#525252] mt-0.5 leading-snug"
               data-testid="eba1-module-detail">
              {modules.detail}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(modules.enabled_modules || []).map((m) => (
                <span key={m.code}
                      className="text-[10px] font-bold px-2 py-1 bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534]"
                      data-testid={`eba1-module-${m.code}`}>
                  {m.name}
                </span>
              ))}
            </div>
            {(modules.commercial_module_lines || []).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                  Priced separately in billing
                </p>
                <ul className="text-[11px] text-[#525252] list-disc list-inside mt-0.5">
                  {modules.commercial_module_lines.map((l) => (
                    <li key={l.id}>{l.description}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Historical evidence: shown, flagged, never offered ─────── */}
      {history.length > 0 && (
        <div className="border-t border-[#E5E5E5] p-4" data-testid="eba1-historical-evidence">
          <p className="text-[11px] font-black uppercase tracking-wider text-[#525252]">
            Historical commercial evidence
          </p>
          <p className="text-[10px] text-[#A3A3A3] mt-0.5">
            Past terms, for provenance. Never offered as a current renewal quantity.
          </p>
          <div className="mt-2 space-y-2">
            {history.map((h) => (
              <div key={h.spec_line_id}
                   className="border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2"
                   data-testid={`eba1-history-${h.spec_line_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                      {h.source_reference || "No document reference"}
                      {h.period_start ? ` · ${h.period_start} – ${h.period_end || "?"}` : ""}
                    </p>
                    {/* Verbatim source text. Where a document stated two user
                        populations, this is where both are visible — the
                        quantity column can only ever hold one number. */}
                    <p className="text-[11px] text-[#0A0A0A] mt-1 break-words">
                      {h.description}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                      Recorded then
                    </div>
                    <div className={`text-sm font-black font-mono ${
                      h.disputed ? "text-[#B91C1C] line-through" : "text-[#525252]"}`}
                      data-testid={`eba1-history-qty-${h.spec_line_id}`}>
                      {num(h.recorded_licensed_user_quantity)}
                    </div>
                  </div>
                </div>
                {h.disputed && (
                  <div className="mt-2 border border-[#FCA5A5] bg-[#FEF2F2] px-2.5 py-2">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#B91C1C] flex items-center gap-1">
                      <Prohibit size={12} weight="fill" aria-hidden="true" />
                      Not a valid licensed-user quantity
                    </p>
                    {h.rulings.map((r, i) => (
                      <p key={i} className="text-[11px] text-[#525252] mt-1 leading-snug break-words">
                        {r.interpretation_notes}
                        {r.assumption_notes ? ` ${r.assumption_notes}` : ""}
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#A3A3A3] mt-1.5">{h.why_not_a_candidate}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <QuantityDialog
        line={entryFor}
        onOpenChange={(open) => !open && setEntryFor(null)}
        onSubmit={(q) => confirm(entryFor, "operator_entered", q)}
      />
    </div>
  );
}

/**
 * Typing a quantity is a separate, deliberate act from adopting one.
 * The dialog states what provisioning says so the operator is choosing
 * against the evidence rather than in ignorance of it — but it never
 * pre-fills that number, because a pre-filled value is a default, and a
 * default is the system deciding.
 */
function QuantityDialog({ line, onOpenChange, onSubmit }) {
  const [value, setValue] = useState("");

  useEffect(() => { setValue(""); }, [line]);

  if (!line) return null;
  const parsed = value.trim() === "" ? null : Number(value);
  const valid = parsed !== null && Number.isFinite(parsed) && parsed >= 0;

  return (
    <Dialog open={!!line} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle>Enter the licensed-user quantity</DialogTitle>
          <DialogDescription className="text-xs">
            Record what the commercial document or agreement states. This is a
            commercial record, not a provisioning setting — it changes no price
            and no annual value.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-[#525252] break-words">{line.description}</p>
          <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              For reference · eBA 1.0 provisioning
            </p>
            <p className="text-sm font-black font-mono text-[#0A0A0A]">
              {num(line.provisioning_licensed_user_limit)}
            </p>
          </div>
          <div>
            <Label htmlFor="eba1-qty"
                   className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">
              Licensed users
            </Label>
            <Input id="eba1-qty" type="number" min="0" step="1" value={value}
                   onChange={(e) => setValue(e.target.value)}
                   className="rounded-none mt-1" data-testid="eba1-quantity-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}
                  className="bg-white border border-[#E5E5E5] rounded-none">Cancel</Button>
          <Button onClick={() => onSubmit(parsed)} disabled={!valid}
                  className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none"
                  data-testid="eba1-quantity-save">
            Record quantity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
